import * as React from "react";
import { AlertTriangle, Loader2, Split } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface PdfPageRange {
  startPage: number;
  endPage: number;
}

const MAX_SPLIT_BILLS = 10;

export function buildPdfPageRanges(
  totalPages: number,
  laterInvoiceStartPages: string,
): PdfPageRange[] {
  if (!Number.isInteger(totalPages) || totalPages < 2) {
    throw new Error("This PDF needs at least two pages to split");
  }
  const tokens = laterInvoiceStartPages
    .split(/[\s,]+/)
    .map((token) => token.trim())
    .filter(Boolean);
  if (tokens.length === 0) {
    throw new Error("Enter where the second invoice starts");
  }
  const startPages = tokens.map((token) => Number(token));
  if (
    startPages.some((page) => !Number.isInteger(page) || page < 2 || page > totalPages)
  ) {
    throw new Error(`Start pages must be whole numbers from 2 through ${totalPages}`);
  }
  const uniqueSorted = [...new Set(startPages)].sort((a, b) => a - b);
  if (
    uniqueSorted.length !== startPages.length ||
    uniqueSorted.some((page, index) => page !== startPages[index])
  ) {
    throw new Error("Enter each start page once, in increasing order");
  }
  if (uniqueSorted.length + 1 > MAX_SPLIT_BILLS) {
    throw new Error(`A PDF can be split into at most ${MAX_SPLIT_BILLS} bills at once`);
  }

  const allStarts = [1, ...uniqueSorted];
  return allStarts.map((startPage, index) => ({
    startPage,
    endPage: allStarts[index + 1] ? allStarts[index + 1]! - 1 : totalPages,
  }));
}

function pageRangeLabel(range: PdfPageRange): string {
  return range.startPage === range.endPage
    ? `Page ${range.startPage}`
    : `Pages ${range.startPage}–${range.endPage}`;
}

export function SplitPdfDialog({
  open,
  onOpenChange,
  filename,
  totalPages,
  isSubmitting,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filename: string;
  totalPages: number;
  isSubmitting: boolean;
  onConfirm: (ranges: PdfPageRange[]) => Promise<void>;
}) {
  const [laterStartPages, setLaterStartPages] = React.useState("");
  const [validationError, setValidationError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setLaterStartPages(totalPages === 2 ? "2" : "");
    setValidationError(null);
  }, [open, totalPages]);

  let preview: PdfPageRange[] = [];
  if (laterStartPages.trim()) {
    try {
      preview = buildPdfPageRanges(totalPages, laterStartPages);
    } catch {
      preview = [];
    }
  }

  const handleConfirm = async () => {
    let ranges: PdfPageRange[];
    try {
      ranges = buildPdfPageRanges(totalPages, laterStartPages);
      setValidationError(null);
    } catch (error) {
      setValidationError(
        error instanceof Error ? error.message : "Check the invoice start pages",
      );
      return;
    }
    await onConfirm(ranges);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isSubmitting) onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Split className="h-5 w-5" />
            Split PDF into bills
          </DialogTitle>
          <DialogDescription>
            Create one bill and PDF per invoice. Mark the first page of each
            invoice after the first in {filename}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="split-pdf-start-pages">
              Later invoices start on pages
            </Label>
            <Input
              id="split-pdf-start-pages"
              inputMode="numeric"
              placeholder={totalPages >= 5 ? "For example: 3, 5" : "For example: 2"}
              value={laterStartPages}
              aria-invalid={validationError !== null}
              aria-describedby={
                validationError
                  ? "split-pdf-help split-pdf-error"
                  : "split-pdf-help"
              }
              disabled={isSubmitting}
              onChange={(event) => {
                setLaterStartPages(event.target.value);
                setValidationError(null);
              }}
            />
            <p id="split-pdf-help" className="text-muted-foreground text-xs">
              This PDF has {totalPages} pages. Example: 3, 5 creates bills for
              pages 1–2, 3–4, and 5 through the end.
            </p>
            {validationError && (
              <p id="split-pdf-error" className="text-destructive text-xs" role="alert">
                {validationError}
              </p>
            )}
          </div>

          {preview.length >= 2 && (
            <div className="rounded-md border">
              <p className="border-b px-3 py-2 text-sm font-medium">
                {preview.length} new bills
              </p>
              <ol className="divide-y text-sm">
                {preview.map((range, index) => (
                  <li
                    key={range.startPage}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <span>Bill {index + 1}</span>
                    <span className="text-muted-foreground tabular-nums">
                      {pageRangeLabel(range)}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              The combined bill will be kept as voided. Each split bill will
              keep the saved vendor and notes, then read its own invoice number,
              dates, total, and GL details for review.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isSubmitting || !laterStartPages.trim()}
            onClick={() => void handleConfirm()}
          >
            {isSubmitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Split className="mr-2 h-4 w-4" />
            )}
            {isSubmitting
              ? "Splitting…"
              : preview.length >= 2
                ? `Split into ${preview.length} bills`
                : "Split PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
